import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';
import {
  getMatchCardFinishedStyles,
  type MatchCardFinishedStyleOptions,
} from './MatchCardFinished.styles';

export type { MatchCardLayoutMode } from './MatchCardFinished.styles';

export const getMatchCardStyles = (
  theme: Theme,
  themeMode: ThemeMode = 'light',
  options?: MatchCardFinishedStyleOptions,
) => {
  const isDark = themeMode === 'dark';
  const tutorialWide = options?.layout === 'tutorial';

  return {
    ...StyleSheet.create({
      card: {
        borderRadius: 24,
        backgroundColor: theme.background.card,
        overflow: 'hidden',
        width: tutorialWide ? '100%' : '94%',
        alignSelf: tutorialWide ? 'stretch' : 'center',
        marginBottom: 14,
        borderWidth: 0,
        shadowColor: theme.shadow,
        shadowOffset: { width: 0, height: isDark ? 4 : 8 },
        shadowOpacity: isDark ? 0.4 : 0.12,
        shadowRadius: isDark ? 12 : 14,
        elevation: isDark ? 6 : 10,
        ...(Platform.OS === 'web' && {
          boxShadow: isDark
            ? ('0 8px 28px rgba(0,0,0,0.5)' as never)
            : ('0 8px 24px rgba(0, 0, 0, 0.1)' as never),
          maxWidth: tutorialWide ? ('100%' as never) : 520,
        }),
      },
      cardBoosted: {
        borderWidth: 1,
        borderColor: theme.accent + '88',
        shadowColor: theme.accent,
        shadowOpacity: 0.45,
        shadowRadius: 18,
        ...(Platform.OS === 'web' && {
          boxShadow: `0 0 22px 3px ${theme.accent}66` as never,
        }),
      },
      cardContent: {
        zIndex: 1,
        paddingTop: 8,
        paddingBottom: 0,
      },
      cardTopBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingTop: 4,
        paddingBottom: 2,
      },
      cardTopBarSpacer: {
        flex: 1,
      },
      cardTopBarRight: {
        flex: 1,
        alignItems: 'flex-end',
      },
      stageBadgeOnPurple: {
        alignSelf: 'flex-start',
        maxWidth: '100%',
        backgroundColor: isDark ? 'rgba(0,0,0,0.42)' : 'rgba(255,255,255,0.24)',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 16,
        borderWidth: 0,
      },
      stageBadgeOnPurpleText: {
        color: theme.white,
        fontSize: 11,
        fontWeight: '700',
        textShadowColor: 'rgba(0,0,0,0.5)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      },
      stageRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
        flex: 1,
        flexShrink: 1,
        minWidth: 0,
        flexWrap: 'wrap',
      },
      boostMiniBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: theme.accent + '66',
        backgroundColor: theme.accent + '2A',
        paddingHorizontal: 6,
        paddingVertical: 3,
      },
      boostMiniBadgeText: {
        fontSize: 10,
        fontWeight: '800',
        color: theme.white,
        textShadowColor: 'rgba(0,0,0,0.35)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
      },
      pointsCluster: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        flexGrow: 0,
        paddingTop: 2,
      },
      pointsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        flexShrink: 0,
      },
      exactPointsText: {
        color: theme.success,
        fontSize: 13,
        fontWeight: '800',
        textShadowColor: 'rgba(0,0,0,0.45)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      },
      pointsDivider: {
        color: theme.white,
        fontSize: 13,
        fontWeight: '700',
        opacity: 0.9,
        textShadowColor: 'rgba(0,0,0,0.45)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
      },
      resultPointsText: {
        color: theme.info,
        fontSize: 13,
        fontWeight: '800',
        textShadowColor: 'rgba(0,0,0,0.45)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      },
      matchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        paddingHorizontal: 4,
        marginTop: 10,
        marginBottom: 8,
      },
      teamBlock: {
        width: 78,
        flexGrow: 0,
        flexShrink: 0,
        alignItems: 'center',
        gap: 4,
      },
      flagWrapper: {
        borderRadius: 8,
        borderWidth: 0,
        padding: 2,
        backgroundColor: theme.background.card,
        shadowColor: theme.shadow,
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: isDark ? 0.25 : 0.12,
        shadowRadius: 4,
        elevation: 3,
      },
      teamName: {
        fontSize: 12,
        fontWeight: '800',
        color: theme.black,
        textAlign: 'center',
        alignSelf: 'stretch',
      },
      bottomRow: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'stretch',
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: isDark ? 'rgba(255,255,255,0.10)' : theme.gray[200],
      },
      cardActionDivider: {
        width: StyleSheet.hairlineWidth,
        alignSelf: 'stretch',
        backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : theme.gray[200],
      },
      cardActionButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 13,
        paddingHorizontal: 8,
      },
      cardActionButtonPressed: {
        opacity: 0.65,
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : theme.gray[100],
      },
      cardActionIcon: {
        color: isDark ? theme.gray[600] : theme.gray[500],
      },
      cardActionText: {
        fontSize: 12,
        fontWeight: '600',
        color: isDark ? theme.gray[700] : theme.gray[600],
        letterSpacing: 0.1,
      },
      boostButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        borderRadius: 14,
        paddingVertical: 5,
        paddingHorizontal: 8,
        backgroundColor: theme.accent + '1A',
        borderWidth: 1.5,
        borderColor: theme.accent + '99',
      },
      boostButtonActive: {
        backgroundColor: theme.accent,
        borderWidth: 1.5,
        borderColor: theme.accentDark,
        shadowColor: theme.accent,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.9,
        shadowRadius: 12,
        elevation: 8,
        ...(Platform.OS === 'web' && {
          boxShadow: `0 0 14px 4px ${theme.accent}BB` as never,
        }),
      },
      boostButtonDisabled: {
        backgroundColor: 'rgba(0,0,0,0.14)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
      },
      boostButtonPressed: { opacity: 0.75, transform: [{ scale: 0.91 }] },
      boostButtonHover: {
        ...(Platform.OS === 'web' && { opacity: 0.85 }),
      },
      boostX2Multiply: {
        fontSize: 9,
        fontWeight: '700',
        color: theme.accent,
        lineHeight: 14,
      },
      boostX2MultiplyActive: {
        color: theme.textOnCard,
      },
      boostX2Digit: {
        fontSize: 13,
        fontWeight: '900',
        color: theme.accent,
        lineHeight: 14,
        letterSpacing: -0.5,
      },
      boostX2DigitActive: {
        color: theme.textOnCard,
      },
      boostX2Disabled: {
        color: 'rgba(255,255,255,0.2)',
      },
      groupPosition: {
        fontSize: 10,
        fontWeight: '700',
        color: isDark ? theme.gray[500] : theme.gray[600],
        textAlign: 'center',
      },
    }),
    ...getMatchCardFinishedStyles(theme, themeMode, options),
  };
};
